/**
 * Orchestrateur de session : relie le signaling, WebRTC et le moteur de transfert.
 *
 * C'est le seul module qui connaît l'enchaînement complet. Les couches inférieures
 * s'ignorent mutuellement : `signaling.ts` ne sait rien de WebRTC, `peer.ts` ne sait rien
 * du transfert de fichiers, et le moteur de transfert ne sait rien du réseau.
 *
 * ## Le point délicat : l'acceptation côté récepteur
 *
 * Le récepteur doit obtenir une autorisation d'écriture disque (`showSaveFilePicker`),
 * qui exige une **activation utilisateur transitoire**. Or la demande arrive par le réseau,
 * pas par un clic. D'où la séquence, imposée par la plateforme :
 *
 * 1. le `meta` arrive → on affiche la demande et on **attend** ;
 * 2. l'utilisateur clique « Accepter » → `createSinkSet` est appelé **dans le geste** ;
 * 3. seulement alors on répond `accept` → l'émetteur démarre.
 *
 * Inverser 2 et 3 ferait démarrer l'émetteur avant que le disque soit prêt, et l'appel au
 * sélecteur échouerait faute d'activation.
 */

import {
  buildShareUrl,
  buildKeyFragment,
  formatBytes,
  formatDuration,
  generateKeyBytes,
  randomRoomId,
  RELAY_ASSUMED_BYTES_PER_SECOND,
  RELAY_NOTICE_THRESHOLD_BYTES,
  type Bytes,
  type DcControl,
  type ErrorCode,
  type FileMeta,
  type IceConfig,
  type RejectReason,
  type Role,
  type RoomPolicy,
  type SignalPayload,
} from '@attrape/shared';
import { decideWriteStrategy, detectCapabilities } from './core/browser.js';
import { importAesKey } from './core/crypto.js';
import { FrameCodec } from './core/framing.js';
import { fetchIceConfig } from './core/ice.js';
import { PeerSession } from './core/peer.js';
import { RelayTransport } from './core/relay.js';
import { SignalingClient } from './core/signaling.js';
import { TransferChannel, waitForControl } from './core/transfer/channel.js';
import { runReceiver, type ReceiverResumeContext } from './core/transfer/receiver.js';
import {
  chunkCountFor,
  createBitmap,
  deleteSession,
  loadSession,
  missingCount,
  missingRanges,
} from './core/transfer/resume-store.js';
import {
  canPersistProgress,
  canResumeWithoutPrompt,
  decideOnPeerLeft,
  shouldSurfaceError,
} from './core/transfer/reconnect.js';
import { buildFileMeta, runSender } from './core/transfer/sender.js';
import { DestinationCancelledError, createSinkSet, type SinkSet } from './core/transfer/writer.js';
import type { Transport } from './core/transport.js';
import { msg, toDescriptor, type MessageDescriptor } from './i18n/index.js';
import { useAppStore } from './store.js';

/** Délai laissé au destinataire pour accepter, avant abandon. */
const ACCEPT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Durée maximale d'une tentative de reconnexion, avant d'abandonner franchement.
 *
 * Une reconnexion **infinie** serait un mensonge : si le correspondant a fermé son
 * onglet, il ne reviendra pas, et l'utilisateur verrait « reprise en cours… » pendant
 * des heures. Au-delà de ce délai, on passe en erreur avec un message qui dit quoi
 * faire (relancer le partage).
 *
 * Cinq minutes : assez pour couvrir une bascule Wi-Fi/4G, une mise en veille courte ou
 * un redémarrage de box ; assez court pour ne pas laisser croire que ça reprendra
 * toujours.
 */
const RECONNECT_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Fichiers retenus entre la page d'accueil et la salle.
 *
 * Un objet de module plutôt qu'un état React : la navigation interne au SPA conserve le
 * contexte JavaScript, donc les objets `File` restent valides. Ils **ne survivent pas à
 * un rechargement de page** — c'est une garantie du navigateur, pas une négligence. Le
 * marqueur ci-dessous permet de l'expliquer à l'utilisateur au lieu de le laisser croire
 * qu'il est devenu destinataire.
 */
interface PendingTransfer {
  roomId: string;
  files: File[];
}

let pendingTransfer: PendingTransfer | null = null;

const SENDER_MARKER_KEY = 'attrape:sender-room';

function stageFiles(roomId: string, files: File[]): void {
  pendingTransfer = { roomId, files };
}

/** Récupère les fichiers en attente pour une salle. Ne les consomme pas : idempotent. */
export function getStagedFiles(roomId: string): File[] | null {
  if (pendingTransfer?.roomId !== roomId) return null;
  return pendingTransfer.files;
}

/** Marque une salle comme créée par cet onglet, y compris à travers un rechargement. */
function markAsSender(roomId: string): void {
  try {
    sessionStorage.setItem(SENDER_MARKER_KEY, roomId);
  } catch {
    // Navigation privée ou stockage désactivé : on perd l'information, sans gravité.
  }
}

export function wasSender(roomId: string): boolean {
  try {
    return sessionStorage.getItem(SENDER_MARKER_KEY) === roomId;
  } catch {
    return false;
  }
}

/**
 * Prépare une nouvelle salle de partage à partir d'une sélection de fichiers.
 *
 * Renvoie à la fois l'URL absolue (à copier et envoyer) et le chemin relatif (pour la
 * navigation interne au SPA, qui doit conserver le fragment porteur de la clé).
 */
export function createShareLink(
  files: File[],
  /**
   * Politique d'usage du lien. Par défaut `one-shot` : le lien meurt après son premier
   * destinataire. C'est le défaut du serveur, et le choix sûr — un lien qui fuit
   * (historique, presse-papiers, conversation) reste sinon exploitable jusqu'à
   * l'expiration de la salle.
   */
  policy: RoomPolicy = 'one-shot',
): {
  roomId: string;
  key: Bytes;
  url: string;
  path: string;
} {
  const roomId = randomRoomId();
  const key = generateKeyBytes();
  const fragment = buildKeyFragment(key);
  const url = buildShareUrl(window.location.origin, roomId, key);
  stageFiles(roomId, files);
  markAsSender(roomId);
  rememberPolicy(roomId, policy);
  return { roomId, key, url, path: `/t/${roomId}${fragment}` };
}

/**
 * Politique retenue pour chaque salle créée par cet onglet.
 *
 * En `sessionStorage` plutôt qu'en mémoire : la page de salle peut être rechargée, et
 * l'émetteur doit alors réannoncer la même politique. Réannoncer `one-shot` après avoir
 * créé un lien `multi` le dégraderait silencieusement — mais la politique ne peut pas
 * changer après la création côté serveur, donc l'effet réel est nul. Le conserver évite
 * surtout d'afficher une interface qui mentirait sur ce qui a été créé.
 */
const POLICY_PREFIX = 'attrape:policy:';

function rememberPolicy(roomId: string, policy: RoomPolicy): void {
  try {
    sessionStorage.setItem(`${POLICY_PREFIX}${roomId}`, policy);
  } catch {
    // Navigation privée ou stockage désactivé : on perd l'information, sans gravité —
    // le serveur a déjà retenu la politique à la création.
  }
}

/** Politique d'une salle créée par cet onglet. Défaut sûr si inconnue. */
export function policyFor(roomId: string): RoomPolicy {
  try {
    const stored = sessionStorage.getItem(`${POLICY_PREFIX}${roomId}`);
    return stored === 'multi' ? 'multi' : 'one-shot';
  } catch {
    return 'one-shot';
  }
}

// ─────────────────────────────────────────────────────────────────────────────

export interface ShareSessionOptions {
  roomId: string;
  key: Bytes;
  role: Role;
  /** Politique annoncée au serveur. Défaut sûr si omise. */
  policy?: RoomPolicy;
}

export class ShareSession {
  private readonly store = useAppStore;
  private readonly abort = new AbortController();

  private signaling: SignalingClient | null = null;
  private peer: PeerSession | null = null;
  private channel: TransferChannel | null = null;
  /**
   * Désabonnement à la fermeture du canal applicatif.
   *
   * La fermeture du transport pendant un transfert doit déclencher une reconnexion
   * même sans `peer-left` du serveur : quand c’est notre propre lien qui tombe
   * (WebRTC, relais), le serveur ne signale pas forcément le départ. Sans ce hook, le
   * transport mort n’est jamais réinitialisé et la reprise ne démarre pas.
   */
  private unsubscribeChannelClose: (() => void) | null = null;
  /**
   * Transport de secours. Créé uniquement si le direct échoue **et** que le serveur
   * annonce le relais : dans tous les autres cas, l'échec reste explicite.
   */
  private relayTransport: RelayTransport | null = null;
  /** Empêche une double bascule (échec ICE signalé deux fois par des chemins différents). */
  private relayAttempted = false;
  /**
   * Trames relayées reçues avant l’installation du transport (pendant l’`await` de
   * `onTransportReady` ou avant `onDirectFailure`). Bufférisées puis rejouées dès que le
   * transport est prêt, pour ne pas les acquitter et les perdre.
   */
  private readonly pendingRelayFrames: { seq: number; data: string }[] = [];
  /**
   * La négociation directe est terminale : une fois la bascule vers le relais décidée,
   * les signaux ICE/SDP tardifs déjà en file sur le signaling ne doivent pas recréer une
   * `PeerSession` et relancer un direct concurrent.
   */
  private directNegotiationAbandoned = false;

  private iceConfig: IceConfig | null = null;
  private negotiationStarted = false;
  private receiving: FileMeta[] = [];
  /**
   * Contexte de suivi de la réception, pour la reprise.
   *
   * Renseigné dès qu'un transfert démarre sur une stratégie persistante, et conservé sur
   * `this` : une reconnexion crée une nouvelle `ShareSession` si la page a été rechargée,
   * mais dans le cas d'une simple coupure réseau la session survit et c'est cet objet qui
   * porte l'état. C'est aussi lui qui alimente la persistance IndexedDB au fil de l'eau.
   */
  private resumeContext: ReceiverResumeContext | null = null;

  /**
   * Minuterie bornant la durée d'une reconnexion.
   *
   * Une reconnexion ne doit pas être éternelle : passé un délai, l'utilisateur doit
   * savoir que ça ne reprendra pas tout seul, plutôt que de fixer une barre figée.
   */
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Destinations d'écriture du transfert en cours.
   *
   * Conservées sur `this` pour survivre à une **reconnexion** : après une coupure, il est
   * impossible de rappeler `showSaveFilePicker`, qui exige une activation utilisateur
   * transitoire. Sans ces sinks, le récepteur devrait redemander une autorisation qu'il
   * ne peut pas obtenir, et la reprise automatique serait illusoire.
   *
   * Libérés à la fin du transfert, à son annulation, ou à la destruction de la session.
   */
  private sinks: SinkSet | null = null;
  private disposed = false;

  constructor(private readonly options: ShareSessionOptions) {}

  /** Démarre la session : connexion au signaling, puis attente du correspondant. */
  async start(): Promise<void> {
    const store = this.store.getState();

    // Remise à zéro explicite : une session précédente peut avoir laissé une erreur,
    // et `setError` positionne la phase à `error`. Repartir d'un état propre évite que
    // la nouvelle session hérite de l'échec de la précédente.
    store.reset();
    store.setRole(this.options.role);
    store.setPhase('connecting');

    if (this.options.role === 'sender') {
      store.setShareUrl(
        buildShareUrl(window.location.origin, this.options.roomId, this.options.key),
      );
    }

    this.signaling = new SignalingClient(
      this.options.roomId,
      this.options.role,
      {
        onWelcome: (message) => this.onWelcome(message.peers.length),
        onPeerJoin: () => void this.onPeerAvailable(),
        onPeerLeft: () => this.onPeerLeft(),
        onSignal: (_from, payload) => void this.onSignal(payload),
        onPeerState: () => undefined,
        onRelay: (_from, seq, data) => {
          // Le transport relayé n’est peut-être pas encore installé : une trame peut
          // arriver pendant l’`await` de `onTransportReady`, ou avant que
          // `onDirectFailure` n’ait créé le transport. On bufferise alors la trame
          // jusqu’à l’installation effective, pour ne pas l’acquitter puis la perdre.
          if (this.relayTransport) {
            this.relayTransport.handleInbound(seq, data);
          } else {
            this.pendingRelayFrames.push({ seq, data });
          }
        },
        onRelayAck: (seq) => this.relayTransport?.handleAck(seq),
        onServerError: (code, message) => {
          // Une erreur fatale côté serveur (trame rejetée) ne produit jamais de
          // `relay-ack` : la séquence reste dans `unacked` et le transfert se
          // bloquerait. On ferme le transport relay pour échouer explicitement.
          if (code === 'rate_limited' || code === 'payload_too_large') {
            this.relayTransport?.fail();
            this.relayTransport = null;
          }
          // `room_consumed` après un transfert déjà terminé n'est pas une erreur à montrer.
          //
          // La consommation survient sur la fin d'usage **annoncée** par le destinataire,
          // donc pendant que les deux pairs sont encore connectés. Un hoquet réseau ou une
          // mise en veille juste après un succès fait donc reconnecter le client, le serveur
          // refuse, et l'écran de succès serait remplacé par « ce lien a déjà été utilisé ».
          // Or le transfert a réussi : il n'y a rien à signaler, et rien à réparer.
          if (code === 'room_consumed' && this.store.getState().phase === 'done') return;
          this.store.getState().setError(translateServerError(code, message));
        },
        onStatus: (status) => {
          this.store.getState().setSignalingStatus(status);
          if (status === 'reconnecting') this.store.getState().setPhase('connecting');
        },
        onLatency: (milliseconds) => this.store.getState().setLatency(milliseconds),
      },
      guessDeviceName(),
      this.options.policy ?? policyFor(this.options.roomId),
    );

    this.signaling.connect();
  }

  private onWelcome(peerCount: number): void {
    if (this.disposed) return;

    if (peerCount > 0) {
      // Le correspondant était déjà là : on démarre sans attendre `peer-join`.
      void this.onPeerAvailable();
      return;
    }

    // Revenu du signaling, mais le correspondant n'est pas encore là. En reconnexion, on
    // conserve cet état : le pair n'a pas encore rouvert son lien, et afficher « en
    // attente du destinataire » ferait croire que tout est à refaire.
    if (this.store.getState().phase === 'reconnecting') return;

    this.store.getState().setPhase('waiting-peer');
  }

  private async onPeerAvailable(): Promise<void> {
    if (this.disposed || this.negotiationStarted) return;
    if (this.options.role !== 'sender') {
      // Le récepteur attend l'offre : rien à initier de son côté.
      this.store.getState().setPhase('negotiating');
      return;
    }

    this.negotiationStarted = true;
    this.store.getState().setPhase('negotiating');

    try {
      this.iceConfig ??= await fetchIceConfig(this.abort.signal);
      this.peer = this.createPeerSession();
      await this.peer.start();
    } catch (error) {
      this.store.getState().setError(toDescriptor(error));
    }
  }

  private createPeerSession(): PeerSession {
    return new PeerSession({
      role: this.options.role,
      iceServers: this.iceConfig?.iceServers ?? [],
      sendSignal: (payload) => {
        this.signaling?.send({ t: 'signal', payload });
      },
      onTransportReady: (transport) => void this.onTransportReady(transport),
      onConnectionInfo: (info) => this.store.getState().setConnection(info),
      onFailure: (reason) => void this.onDirectFailure(reason),
    });
  }

  /**
   * Le direct a échoué. Tente le relais si le serveur le propose, sinon échoue.
   *
   * C'est le cœur de la Phase 4 : un réseau qui bloque le P2P (NAT symétrique, pare-feu
   * d'entreprise) doit aboutir à un transfert **relayé, donc chiffré et relisible par
   * personne**, plutôt qu'à un échec sec. Le repli n'est tenté qu'une fois : insister
   * après un échec du relais lui-même ne ferait qu'ajouter de l'attente.
   */
  private async onDirectFailure(reason: MessageDescriptor): Promise<void> {
    if (this.disposed) return;
    if (this.relayAttempted) return;
    // La bascule vers le relais n’a de sens qu’avant qu’un transfert direct n’ait démarré.
    // `onFailure` est aussi appelé depuis `RTCDataChannel.onerror` après l’ouverture du
    // canal : dans ce cas, un flux de transfert tourne déjà. Relancer le relais
    // démarrerait un second flux concurrent (un nouveau `TransferChannel` sur le relais
    // pendant que l’ancien `runSenderFlow`/`runReceiver` tourne encore sur le direct).
    // On laisse donc l’échec explicite mi-transfert plutôt que d’introduire une course.
    if (this.channel !== null) {
      this.store.getState().setError(reason);
      return;
    }
    if (!this.iceConfig?.relay) {
      // Aucun relais annoncé : le message d'échec direct est le plus informatif qui soit.
      this.store.getState().setError(reason);
      return;
    }

    this.relayAttempted = true;
    if (import.meta.env.DEV) console.warn('[relais] bascule après échec du direct');

    // Le pair WebRTC n'a plus d'utilité : on le libère avant d'ouvrir le relais, sinon
    // la collecte ICE continuerait en arrière-plan. La négociation directe est désormais
    // terminale : les signaux ICE/SDP tardifs ne doivent pas la relancer.
    this.peer?.close();
    this.peer = null;
    this.directNegotiationAbandoned = true;

    const relay = new RelayTransport({
      send: (seq, data) => this.signaling?.sendRelay(seq, data) ?? false,
      sendAck: (seq) => {
        this.signaling?.sendRelayAck(seq);
      },
    });
    this.relayTransport = relay;

    // Rejoue les trames reçues pendant l’installation du transport, avant de continuer :
    // elles auraient sinon été acquittées et perdues, tronquant le transfert.
    const pending = this.pendingRelayFrames.splice(0);
    for (const frame of pending) relay.handleInbound(frame.seq, frame.data);

    // L'indicateur de chemin doit dire la vérité : les données passent désormais par le
    // serveur, et l'utilisateur a le droit de le savoir. On synthétise un `ConnectionInfo`
    // de type `relay`, ce qui réutilise l'affichage et le ton d'avertissement existants.
    this.store.getState().setConnection({
      candidateType: 'relay',
      protocol: 'ws',
      throughServer: true,
      state: 'connected',
    });

    this.notifySlowRelay();

    await this.onTransportReady(relay);
  }

  /**
   * Avertit que les données passent par le relais, en chiffrant l'attente quand la taille
   * est connue.
   *
   * Le relais existe pour les réseaux qui bloquent le P2P, pas pour rendre confortable un
   * transfert de plusieurs centaines de mégaoctets : à ~40 Ko/s, 120 Mo demandent près
   * d'une heure. Le dire avant vaut mieux que de laisser l'utilisateur découvrir, au bout
   * de 1,2 %, que son transfert n'y arrivera pas.
   */
  private notifySlowRelay(): void {
    const sizes =
      this.options.role === 'sender'
        ? (getStagedFiles(this.options.roomId) ?? []).map((file) => file.size)
        : this.receiving.map((file) => file.size);
    const biggest = sizes.length > 0 ? Math.max(...sizes) : 0;

    const store = this.store.getState();
    if (biggest >= RELAY_NOTICE_THRESHOLD_BYTES) {
      store.setWarning(
        msg.key('room.relaySlowBig', {
          size: formatBytes(biggest),
          eta: formatDuration(Math.round(biggest / RELAY_ASSUMED_BYTES_PER_SECOND)),
        }),
      );
      return;
    }
    store.setWarning(msg.key('room.relaySlow'));
  }

  private async onSignal(payload: SignalPayload): Promise<void> {
    if (this.disposed) return;

    // La négociation directe est abandonnée (bascule vers le relais) : les signaux
    // ICE/SDP tardifs déjà en file sur le signaling ne doivent pas recréer une
    // `PeerSession` et relancer un direct concurrent du relais.
    if (this.directNegotiationAbandoned) return;

    // Le récepteur crée sa session à la première offre reçue : il n'a personne à
    // contacter avant, et allouer une `RTCPeerConnection` à vide serait inutile.
    if (!this.peer) {
      try {
        this.iceConfig ??= await fetchIceConfig(this.abort.signal);
        this.peer = this.createPeerSession();
      } catch (error) {
        this.store.getState().setError(toDescriptor(error));
        return;
      }
    }

    await this.peer.handleSignal(payload);
  }

  private async onTransportReady(transport: Transport): Promise<void> {
    if (this.disposed) return;

    // Le lien est rétabli : la reconnexion a réussi, la limite n'a plus lieu d'être.
    this.clearReconnectTimeout();

    const key = await importAesKey(this.options.key);
    const codec = new FrameCodec(key);

    if (this.options.role === 'sender') {
      const files = getStagedFiles(this.options.roomId);
      if (!files || files.length === 0) {
        transport.close();
        this.store.getState().setError(msg.key('errors.filesForgotten'));
        return;
      }

      this.channel = new TransferChannel(transport, codec, {
        onProtocolError: (detail) => this.handleProtocolError(detail),
      });
      this.armChannelClose();

      void this.runSenderFlow(files);
      return;
    }

    this.channel = new TransferChannel(transport, codec, {
      onControl: (control) => void this.onReceiverControl(control),
      onProtocolError: (detail) => this.handleProtocolError(detail),
    });
    this.armChannelClose();
  }

  /**
   * Traite une anomalie protocolaire.
   *
   * Le détail (« séquence reçue 42, attendue 43 ») ne veut rien dire pour l'utilisateur :
   * il part au journal de développement, où il est exploitable, et l'écran affiche une
   * phrase qui dit quoi faire. Afficher le détail brut serait aussi inutile que de le
   * taire complètement.
   */
  private handleProtocolError(detail: string): void {
    if (import.meta.env.DEV) console.error('[transfert] anomalie protocolaire :', detail);
    // Une anomalie en cours de transfert, c'est d'abord un flux interrompu : le dire avec
    // le point d'arrêt réel (« interrompu à 42 % ») aide l'utilisateur, là où « données non
    // conformes » lui fait croire à une corruption imaginaire.
    const progress = this.store.getState().progress;
    if (progress && progress.totalTotal > 0 && progress.totalBytes > 0) {
      const percent = (progress.totalBytes / progress.totalTotal) * 100;
      this.store.getState().setError(
        msg.key('errors.transferInterruptedAt', {
          received: formatBytes(progress.totalBytes),
          total: formatBytes(progress.totalTotal),
          percent: percent < 1 ? '< 1' : String(Math.round(percent)),
        }),
      );
      return;
    }
    this.store.getState().setError(msg.key('errors.integrityFailed'));
  }

  /**
   * Faut-il afficher cette erreur, ou une reconnexion est-elle déjà en cours ?
   *
   * Quand le lien tombe, la réception et l'envoi échouent **tous les deux** — c'est
   * inévitable, ils dépendent du même canal. Sans ce filtre, l'échec technique écraserait
   * l'état « reprise en cours » par un message d'erreur, et l'utilisateur croirait le
   * transfert perdu alors que le système est en train de le rattraper.
   *
   * On ne masque pas l'erreur : elle reste au journal de développement, où elle est
   * exploitable, et l'écran garde l'information utile (« ça reprend »).
   */
  private shouldReportError(): boolean {
    if (shouldSurfaceError(this.store.getState().phase)) return true;
    if (import.meta.env.DEV) {
      console.warn('[session] échec technique pendant une reconnexion — non affiché');
    }
    return false;
  }

  // ── Émetteur ──────────────────────────────────────────────────────────────

  private async runSenderFlow(files: File[]): Promise<void> {
    const channel = this.channel;
    if (!channel) return;

    try {
      const metas = buildFileMeta(files);
      await channel.sendControl({ t: 'meta', files: metas }, this.abort.signal);

      // Attendre la décision du destinataire avant de consommer de la bande passante.
      // Trois réponses possibles : il accepte, il refuse, ou il demande à reprendre —
      // auquel cas il annonce ce qui lui manque.
      const decision = await waitForControl(
        channel,
        (control) =>
          control.t === 'accept' || control.t === 'reject' || control.t === 'resume-request',
        {
          signal: this.abort.signal,
          timeoutMs: ACCEPT_TIMEOUT_MS,
          timeoutMessage: msg.key('errors.acceptTimeout'),
        },
      );

      if (decision.t === 'reject') {
        this.store.getState().setError(rejectReasonDescriptor(decision.reason));
        return;
      }

      const resumeRanges = this.resolveResumeRanges(decision, files, metas);

      this.store.getState().setPhase('transferring');

      await runSender({
        channel,
        files,
        signal: this.abort.signal,
        ...(resumeRanges ? { resumeRanges } : {}),
        onProgress: (progress) => {
          this.store.getState().setProgress({
            fileName: progress.fileName,
            fileIndex: progress.fileIndex,
            fileCount: progress.fileCount,
            fileBytes: progress.fileBytes,
            fileTotal: progress.fileTotal,
            totalBytes: progress.totalSent,
            totalTotal: progress.totalBytes,
            bytesPerSecond: progress.bytesPerSecond,
            etaSeconds: progress.etaSeconds,
          });
        },
      });

      this.store.getState().setPhase('done');
    } catch (error) {
      if (isAbort(error)) return;
      // Un échec pendant une reconnexion n'est pas une erreur pour l'utilisateur :
      // la reprise est en cours et le travail déjà fait est conservé.
      if (!this.shouldReportError()) return;
      this.store.getState().setError(toDescriptor(error));
    }
  }

  /**
   * Valide une demande de reprise et la traduit en plages d'envoi.
   *
   * ## La vérification qui compte
   *
   * Le récepteur demande à reprendre à partir de morceaux qu'il a déjà. Mais il ne
   * connaît pas le fichier source : si l'utilisateur a modifié son fichier entre-temps,
   * les morceaux déjà reçus ne correspondent plus, et compléter avec les nouveaux
   * produirait un fichier **silencieusement corrompu** — le pire résultat possible.
   *
   * On vérifie donc `name`, `size` et `lastModified` avant d'accepter. C'est exactement
   * la contrainte énoncée par le `PLAN.md` §6.6. En cas de désaccord, on refuse la reprise
   * et on renvoie tout : coûteux, mais correct.
   *
   * Renvoie `null` quand il n'y a pas de reprise à faire, auquel cas l'envoi est complet.
   */
  private resolveResumeRanges(
    decision: DcControl,
    files: File[],
    metas: FileMeta[],
  ): Map<number, [number, number][]> | null {
    if (decision.t !== 'resume-request') return null;

    const ranges = new Map<number, [number, number][]>();

    for (const request of decision.files) {
      const file = files[request.fileIndex];
      const meta = metas[request.fileIndex];
      if (!file || !meta) continue;

      // Le fichier source est-il le même que celui dont le récepteur possède des
      // morceaux ? Sans cette vérification, une reprise sur un fichier modifié produit
      // un résultat faux sans que rien ne le signale.
      const sameSource =
        meta.name === request.source.name &&
        meta.size === request.source.size &&
        meta.lastModified === request.source.lastModified;

      if (!sameSource) {
        // On n'interrompt pas la session : on ignore simplement la reprise pour ce
        // fichier, et il sera renvoyé **entièrement**. L'utilisateur obtient un fichier
        // correct, au prix d'un transfert plus long — préférable à une erreur sèche qui
        // l'obligerait à recréer le lien de partage.
        if (import.meta.env.DEV) {
          console.warn(
            `[reprise] fichier source différent pour « ${meta.name} » : ` +
              `${request.source.name} (${request.source.size} o, ${request.source.lastModified}) ` +
              `≠ ${meta.name} (${meta.size} o, ${meta.lastModified}). Envoi complet.`,
          );
        }
        continue;
      }

      ranges.set(request.fileIndex, request.ranges);
    }

    return ranges.size > 0 ? ranges : null;
  }

  // ── Récepteur ─────────────────────────────────────────────────────────────

  /**
   * Annonce au serveur que le destinataire a fini d'utiliser le lien.
   *
   * C'est **le** signal de consommation d'une salle à usage unique, et il est délibéré :
   * `done` après un transfert complet, `cancelled` après un refus explicite. Le serveur
   * ne consomme rien sur la fermeture d'une socket — sans quoi une bascule réseau ou un
   * rechargement de page grillerait le lien, et la reprise après coupure serait
   * inatteignable sur une salle `one-shot`, qui est le défaut.
   *
   * Un échec technique, lui, n'est pas annoncé. Le lien n'a rien servi : le brûler
   * forcerait à en recréer un pour une cause transitoire, sans rien apporter à la fenêtre
   * d'exploitation, déjà bornée par l'expiration de la salle.
   */
  private announceEndOfUse(state: 'done' | 'cancelled'): void {
    if (this.options.role !== 'receiver') return;
    this.signaling?.sendPeerState(state);
  }

  /**
   * Reprend une réception interrompue, sans repasser par l'utilisateur.
   *
   * Appelé quand l'émetteur renvoie ses métadonnées après une coupure. On réutilise les
   * destinations déjà ouvertes et le bitmap déjà constitué : rien à redemander, rien à
   * recréer. La seule chose à faire est de dire à l'émetteur ce qui manque.
   */
  private async resumeReceiving(files: FileMeta[]): Promise<void> {
    const channel = this.channel;
    const sinks = this.sinks;
    const resume = this.resumeContext;
    if (!channel || !sinks || !resume) return;

    try {
      const requests = files
        .map((meta, fileIndex) => {
          const bitmap = resume.bitmaps.get(fileIndex);
          if (!bitmap) return null;
          const ranges = missingRanges(bitmap, chunkCountFor(meta.size));
          return {
            fileIndex,
            source: { name: meta.name, size: meta.size, lastModified: meta.lastModified },
            ranges,
          };
        })
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
        .filter((entry) => entry.ranges.length > 0);

      await channel.sendControl({ t: 'resume-request', files: requests }, this.abort.signal);
      this.store.getState().setPhase('transferring');

      const result = await runReceiver({
        channel,
        files,
        sinks: sinks.sinks,
        signal: this.abort.signal,
        resume,
        onProgress: (progress) => {
          this.store.getState().setProgress({
            fileName: progress.fileName,
            fileIndex: progress.fileIndex,
            fileCount: progress.fileCount,
            fileBytes: progress.fileBytes,
            fileTotal: progress.fileTotal,
            totalBytes: progress.totalReceived,
            totalTotal: progress.totalBytes,
            bytesPerSecond: progress.bytesPerSecond,
            etaSeconds: progress.etaSeconds,
          });
        },
      });

      this.store.getState().setResult(result);
      this.store.getState().setPhase('done');
      this.sinks = null;
      // Le lien a servi : une salle à usage unique peut être consommée. On l'annonce
      // **après** le transfert, pas au moment où la socket se fermera.
      this.announceEndOfUse('done');
    } catch (error) {
      if (isAbort(error)) return;
      if (!this.shouldReportError()) return;
      this.store.getState().setError(toDescriptor(error));
    }
  }

  private async onReceiverControl(control: DcControl): Promise<void> {
    if (control.t !== 'meta') return;

    const wasReconnecting = this.store.getState().phase === 'reconnecting';
    this.receiving = control.files;

    // Reprise automatique après une coupure.
    //
    // Le récepteur possède déjà ses destinations d'écriture et son bitmap : il n'a rien
    // à redemander à l'utilisateur. C'est indispensable, car `showSaveFilePicker` exige
    // une activation utilisateur transitoire et ne peut pas être rappelé ici — sans ce
    // chemin, la « reprise automatique » se réduirait à une demande d'acceptation que
    // l'utilisateur ne pourrait même pas satisfaire.
    const canAutoResume = canResumeWithoutPrompt({
      hasSinks: this.sinks !== null,
      hasResumeContext: this.resumeContext !== null,
    });
    if (wasReconnecting && canAutoResume) {
      await this.resumeReceiving(control.files);
      return;
    }

    const totalBytes = control.files.reduce((sum, file) => sum + file.size, 0);
    const largest = control.files.reduce((max, file) => Math.max(max, file.size), 0);
    const decision = decideWriteStrategy(control.files.length, largest, detectCapabilities());

    this.store.getState().setIncoming({ files: control.files, totalBytes, decision });

    if (!decision.allowed) {
      // Refus immédiat et expliqué : l'utilisateur n'a pas à attendre pour apprendre que
      // son navigateur ne peut pas recevoir ce fichier.
      this.store.getState().setError(decision.refusalReason ?? msg.key('errors.receiveImpossible'));
      await this.channel?.sendControl({ t: 'reject', reason: 'cannot-receive' });
      this.announceEndOfUse('cancelled');
      return;
    }

    this.store.getState().setWarning(decision.warning ?? null);
    this.store.getState().setPhase('awaiting-decision');
  }

  /**
   * Ouvre le suivi d'un transfert qui démarre de zéro.
   *
   * Le contexte est vide (aucun morceau reçu) mais **existe** : c'est lui qui fait que le
   * récepteur marque les morceaux au fil de l'eau et persiste l'état. Sans lui, une
   * coupure au milieu d'un premier transfert ne laisserait rien à reprendre.
   */
  private startFreshTracking(files: FileMeta[], handle: FileSystemFileHandle | null): void {
    const bitmaps = new Map<number, Uint8Array>();
    for (const [fileIndex, meta] of files.entries()) {
      bitmaps.set(fileIndex, createBitmap(meta.size));
    }
    this.resumeContext = {
      roomId: this.options.roomId,
      bitmaps,
      handle,
    };
  }

  private freshResumeContext(): ReceiverResumeContext | undefined {
    return this.resumeContext ?? undefined;
  }

  /**
   * Cherche une session interrompue pour le lot annoncé.
   *
   * ## Ce qui rend une reprise possible
   *
   * Il faut **le même fichier source** — nom, taille et date de modification — car les
   * morceaux déjà reçus ne valent que pour cette version-là. Un fichier modifié entre
   * deux sessions produirait sinon un assemblage faux sans que rien ne le signale.
   *
   * Il faut aussi une **stratégie d'écriture persistante** : en `memory`, les morceaux
   * reçus vivent dans l'onglet et disparaîtront avec lui. Il n'y a alors rien à reprendre,
   * et prétendre le contraire ferait échouer la vérification d'intégrité à la fin.
   *
   * Seuls les fichiers **déjà entamés** sont retenus. Un fichier jamais reçu n'a pas de
   * bitmap : il sera envoyé entièrement, ce qui est le cas nominal.
   */
  private async prepareResume(files: FileMeta[]): Promise<ReceiverResumeContext | null> {
    const decision = this.store.getState().incoming?.decision;
    // `fs-access` et `directory` écrivent directement sur le disque, donc ce qui a été
    // reçu survit à la fermeture de l'onglet. `memory` et `streamsaver` non : le premier
    // garde tout dans l'onglet, le second a déjà remis le fichier au navigateur.
    const persistent = decision?.strategy === 'fs-access' || decision?.strategy === 'directory';
    if (!persistent) return null;

    const bitmaps = new Map<number, Uint8Array>();

    for (const [fileIndex, meta] of files.entries()) {
      const session = await loadSession(meta.id);
      if (!session) continue;

      // La session vient-elle du MÊME partage ? `fileId` est stable pour un fichier
      // donné, donc deux partages successifs du même fichier désignent la même entrée de
      // stockage. Sans cette vérification, un nouveau lien (nouvelle salle) ressert les
      // morceaux d'un partage précédent : le récepteur croit alors avoir déjà tout reçu,
      // ne demande rien, et refuse le premier morceau comme « en surplus » — un échec
      // immédiat qui accuse à tort le réseau.
      if (session.roomId !== this.options.roomId) {
        // Cette entrée ne concerne pas ce partage : on l'écarte, et on la supprime pour
        // qu'elle ne revienne pas hanter une tentative ultérieure.
        void deleteSession(meta.id);
        continue;
      }

      // Même fichier source ? C'est la seule condition qui rende les morceaux déjà
      // reçus exploitables. `size` et `lastModified` sont comparés en plus du nom :
      // deux fichiers différents peuvent porter le même nom.
      const sameSource =
        session.name === meta.name &&
        session.size === meta.size &&
        session.lastModified === meta.lastModified;
      if (!sameSource) continue;

      // Reste-t-il quelque chose à recevoir ? Un bitmap **plein** signifie que tous les
      // morceaux sont déjà là : il n'y a donc rien à reprendre. Le conserver serait
      // nuisible, pas neutre — les morceaux attendus étant les morceaux *manquants*, la
      // file serait vide et le premier morceau renvoyé serait refusé comme « en surplus ».
      // C'est le cas d'un transfert interrompu juste après le dernier morceau, avant la
      // confirmation de fin : l'état n'a jamais été purgé, et l'essai suivant échoue
      // immédiatement sur un fichier pourtant intact.
      const total = chunkCountFor(meta.size);
      if (missingCount(missingRanges(session.received, total)) === 0) {
        void deleteSession(meta.id);
        continue;
      }

      // Une session sans morceau reçu n'apporte rien : on la traite comme absente.
      bitmaps.set(fileIndex, session.received);
    }

    if (bitmaps.size === 0) return null;

    return {
      roomId: this.options.roomId,
      bitmaps,
      handle: null,
    };
  }

  /**
   * Accepte la réception. **À appeler directement depuis le gestionnaire de clic** :
   * l'appel au sélecteur de destination doit rester dans l'activation utilisateur.
   */
  async acceptOffer(): Promise<void> {
    const channel = this.channel;
    const files = this.receiving;
    if (!channel || files.length === 0) return;

    const decision = this.store.getState().incoming?.decision;
    if (!decision) return;

    let sinks;
    try {
      // Premier appel asynchrone : le sélecteur. Ne rien intercaler avant.
      sinks = await createSinkSet(files, decision.strategy);
    } catch (error) {
      if (error instanceof DestinationCancelledError) {
        // L'utilisateur a fermé le sélecteur : retour à l'état de demande, pas d'erreur.
        this.store.getState().setPhase('awaiting-decision');
        return;
      }
      this.store.getState().setError(toDescriptor(error));
      await channel.sendControl({ t: 'reject', reason: 'destination-unavailable' });
      this.announceEndOfUse('cancelled');
      return;
    }

    this.store.getState().setIncoming(null);
    this.store.getState().setPhase('transferring');

    try {
      // Contexte de reprise : `null` si l'on ne peut pas reprendre, mais aussi si l'on
      // n'a simplement **rien à reprendre** (premier transfert). Dans le second cas il
      // faut tout de même créer le suivi, sinon une coupure en cours de route ne
      // laisserait aucun état à reprendre.
      //
      // La décision est lue **avant** le `setIncoming(null)` ci-dessus : relire l'état
      // maintenant donnerait `undefined` (l'entrée est effacée) et désactiverait la
      // reprise à tort. On passe donc la stratégie déjà capturée.
      const canResume = canPersistProgress(decision?.strategy);
      const resume = canResume ? await this.prepareResume(files) : null;

      // La poignée du récepteur permet de rouvrir le même fichier à la prochaine
      // tentative, au lieu d'en créer un second.
      // Conservés sur `this` : une coupure ne prévient pas, et il sera impossible de
      // rappeler le sélecteur pour les reconstruire.
      this.sinks = sinks;
      const handle = sinks.sinks[0]?.handle ?? null;
      if (resume) {
        resume.handle = handle;
      } else if (canResume) {
        // Premier transfert : on ouvre le suivi pour que les morceaux reçus soient
        // mémorisés au fil de l'eau.
        this.startFreshTracking(files, handle);
      }

      // Faut-il demander une reprise à l'émetteur ? Uniquement si des morceaux manquent
      // et que le fichier a déjà été entamé.
      const resumeContext = resume ?? this.freshResumeContext();

      if (resume) {
        const requests = files
          .map((meta, fileIndex) => {
            const bitmap = resume.bitmaps.get(fileIndex);
            if (!bitmap) return null;
            const ranges = missingRanges(bitmap, chunkCountFor(meta.size));
            // Un fichier sans morceau manquant n'est pas demandé du tout : l'émetteur
            // n'a rien à envoyer pour lui, et sa présence dans le message serait du bruit.
            return {
              fileIndex,
              source: {
                name: meta.name,
                size: meta.size,
                lastModified: meta.lastModified,
              },
              ranges,
              isComplete: ranges.length === 0,
            };
          })
          .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
          .filter((entry) => !entry.isComplete)
          .map(({ fileIndex, source, ranges }) => ({ fileIndex, source, ranges }));

        if (requests.length > 0) {
          await channel.sendControl({ t: 'resume-request', files: requests }, this.abort.signal);
        } else {
          // Tout est déjà sur le disque : rien à recevoir. On le dit à l'émetteur, qui
          // émettra ses contrôles de fin sans envoyer de données.
          await channel.sendControl({ t: 'resume-request', files: [] }, this.abort.signal);
        }
      } else {
        await channel.sendControl({ t: 'accept' }, this.abort.signal);
      }

      const result = await runReceiver({
        channel,
        files,
        sinks: sinks.sinks,
        signal: this.abort.signal,
        ...(resumeContext ? { resume: resumeContext } : {}),
        onProgress: (progress) => {
          this.store.getState().setProgress({
            fileName: progress.fileName,
            fileIndex: progress.fileIndex,
            fileCount: progress.fileCount,
            fileBytes: progress.fileBytes,
            fileTotal: progress.fileTotal,
            totalBytes: progress.totalReceived,
            totalTotal: progress.totalBytes,
            bytesPerSecond: progress.bytesPerSecond,
            etaSeconds: progress.etaSeconds,
          });
        },
      });

      this.store.getState().setResult(result);
      this.store.getState().setPhase('done');
      this.sinks = null;
      // Le lien a servi : une salle à usage unique peut être consommée.
      this.announceEndOfUse('done');
    } catch (error) {
      // Une reconnexion déclenchée par la fermeture du canal (locale, sans `peer-left`
      // serveur) a déjà mis la phase à `reconnecting` **avant** l’échec du transfert. Les
      // destinations doivent alors survivre : la reprise automatique les réutilise via
      // `canResumeWithoutPrompt`. Les abandonner ici détruirait le travail déjà fait —
      // le défaut que la reprise existe pour éviter. Hors reconnexion, l’échec est réel :
      // on libère les destinations pour ne pas laisser un fichier à moitié écrit.
      const reconnecting = this.store.getState().phase === 'reconnecting';
      if (!reconnecting) {
        await sinks.abortAll();
        this.sinks = null;
      }
      if (isAbort(error)) return;
      // Un échec pendant une reconnexion n'est pas une erreur pour l'utilisateur :
      // la reprise est en cours et le travail déjà fait est conservé.
      if (!this.shouldReportError()) return;
      this.store.getState().setError(toDescriptor(error));
    }
  }

  /** Refuse la réception et le fait savoir à l'émetteur. */
  async rejectOffer(): Promise<void> {
    this.store.getState().setIncoming(null);
    await this.channel?.sendControl({ t: 'reject', reason: 'declined' });
    this.store.getState().setPhase('done');
    // Un refus explicite consomme la salle : le lien a été ouvert, il est grillé. C'est
    // un choix assumé — voir le test qui le fige côté registre.
    this.announceEndOfUse('cancelled');
  }

  /**
   * Branche la détection de fermeture du canal applicatif.
   *
   * Abonné **avant** le canal interne de `runSender`/`runReceiver` : la reconnexion se
   * déclenche donc avant l’échec du transfert, ce qui permet à `shouldReportError` de
   * masquer ce dernier (la phase est déjà `reconnecting`).
   */
  private armChannelClose(): void {
    this.unsubscribeChannelClose?.();
    if (!this.channel) return;
    this.unsubscribeChannelClose = this.channel.onClose(() => this.onChannelClosed());
  }

  /**
   * Le canal applicatif s’est fermé.
   *
   * Cause locale (notre WebRTC ou notre relais a lâché) et non message serveur : il
   * faut réagir comme à un départ de pair — réinitialiser le transport mort et préparer
   * la reprise. `decideOnPeerLeft` ignore déjà une reconnexion en cours, donc un
   * `peer-left` serveur qui suivrait ne ferait que repasser ici sans effet.
   */
  private onChannelClosed(): void {
    if (this.disposed) return;
    // On se désabonne : la fermeture est consommée, et `resetTransport` refermera le
    // canal. Rester abonné ferait rejouer la reconnexion sur un second événement.
    this.unsubscribeChannelClose?.();
    this.unsubscribeChannelClose = null;
    this.onPeerLeft();
  }

  private onPeerLeft(): void {
    this.store.getState().setPeer(null);

    // Le correspondant a disparu pendant le transfert. Ce n'est **pas** un échec : c'est
    // le cas nominal d'une coupure réseau (bascule Wi-Fi, mise en veille), et une reprise
    // est possible. On signale l'interruption et on se prépare à reprendre dès qu'il
    // revient, au lieu de tout abandonner.
    const decision = decideOnPeerLeft({
      phase: this.store.getState().phase,
      role: this.options.role,
      alreadyReconnecting: this.reconnectTimer !== null,
    });

    if (decision === 'reconnect') {
      this.beginReconnect();
      return;
    }

    // Hors transfert, un départ est bénin — mais l'état du relais doit être réinitialisé
    // **pour les deux rôles**.
    //
    // C'est une correction issue de la revue de la Phase 4 : un récepteur qui reste dans
    // la page après un `peer-left` conservait `relayAttempted` et `relayTransport`. Il ne
    // pouvait alors plus basculer sur le relais à l'arrivée d'un nouvel émetteur, et
    // l'ancien transport continuait à recevoir les trames d'une session morte.
    this.resetTransport();

    if (decision === 'back-to-waiting') {
      this.store.getState().setPhase('waiting-peer');
    }
  }

  /**
   * Referme le transport et la session WebRTC, sans toucher au signaling.
   *
   * Le signaling se reconnecte tout seul (délai exponentiel avec gigue, voir
   * `signaling.ts`). Ce qu'il faut libérer, c'est la couche WebRTC et le canal applicatif :
   * ils sont liés à une connexion morte et seront recréés lors de la reprise.
   *
   * **On ne touche pas au contexte de reprise** (`resumeContext`) : c'est lui qui porte le
   * bitmap des morceaux déjà reçus, et il doit survivre à la coupure. Le réinitialiser ici
   * reviendrait à jeter le travail déjà fait — exactement ce que la reprise existe pour
   * éviter.
   */
  private resetTransport(): void {
    this.unsubscribeChannelClose?.();
    this.unsubscribeChannelClose = null;
    this.negotiationStarted = false;
    this.relayAttempted = false;
    // Réinitialisé ici, et non seulement à la construction : sans cela, une session qui a
    // abandonné la négociation directe resterait marquée comme telle après le départ du
    // pair, et refuserait de retenter une bascule à l'arrivée d'un nouvel émetteur.
    this.directNegotiationAbandoned = false;
    this.peer?.close();
    this.peer = null;
    this.relayTransport?.close();
    this.relayTransport = null;
    this.channel?.close();
    this.channel = null;
  }

  /**
   * Bascule en mode reconnexion et prépare la reprise.
   *
   * Le travail déjà fait est conservé : c'est tout l'intérêt. La reprise ne renverra que
   * les morceaux manquants, exactement comme après un rechargement — la logique est la
   * même, seule la façon de la déclencher diffère.
   */
  private beginReconnect(): void {
    if (this.disposed) return;

    this.resetTransport();
    this.store.getState().setPhase('reconnecting');
    this.armReconnectTimeout();

    // L'émetteur relance la négociation dès que le pair réapparaît : c'est `onPeerJoin`
    // qui déclenche `onPeerAvailable`, lequel repart de zéro puisque `negotiationStarted`
    // vient d'être remis à `false`.
    //
    // Le récepteur, lui, attend l'offre : il n'a rien à initier. Sa demande de reprise
    // partira d'elle-même à la prochaine session, avec le même bitmap.
    if (import.meta.env.DEV) {
      console.warn('[session] reconnexion : le lien est tombé, reprise préparée');
    }
  }

  /**
   * Borne la durée d'une reconnexion.
   *
   * Sans limite, un correspondant définitivement parti laisserait l'application sur
   * « reprise en cours… » indéfiniment — l'équivalent moderne du transfert bloqué que le
   * `PLAN.md` §1.3 reproche aux services existants.
   */
  private armReconnectTimeout(): void {
    this.clearReconnectTimeout();
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.disposed) return;
      if (this.store.getState().phase !== 'reconnecting') return;

      // Le message existant dit déjà l'essentiel : le correspondant s'est déconnecté et
      // il faut relancer le partage. En inventer un nouveau n'apporterait rien.
      this.store.getState().setError(msg.key('errors.peerLeft'));
    }, RECONNECT_TIMEOUT_MS);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimer === null) return;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  /** Interrompt la session et libère toutes les ressources. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    // Prévenir l'émetteur qu'on s'en va, avant de fermer le canal.
    void this.channel?.sendControl({ t: 'cancel' }).catch(() => {
      // Le canal est peut-être déjà fermé : l'échec est attendu, sans conséquence.
    });

    this.clearReconnectTimeout();
    this.unsubscribeChannelClose?.();
    this.unsubscribeChannelClose = null;
    this.abort.abort();
    this.channel?.close();
    this.relayTransport?.close();
    this.peer?.close();
    this.signaling?.leave();

    // Abandonner les destinations encore ouvertes : quitter la page ne doit pas laisser
    // un fichier à moitié écrit et verrouillé par le navigateur. On ne commit pas — le
    // transfert n'est pas vérifié, et un fichier partiel présenté comme complet serait
    // pire que pas de fichier du tout.
    void this.sinks?.abortAll().catch(() => {
      // Les flux sont peut-être déjà fermés : l'échec est attendu, sans conséquence.
    });
    this.sinks = null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Registre de session
// ─────────────────────────────────────────────────────────────────────────────

interface SessionEntry {
  roomId: string;
  session: ShareSession;
  references: number;
  /** Destruction programmée, annulée si la session est réacquise entre temps. */
  disposeTimer: ReturnType<typeof setTimeout> | null;
}

let currentEntry: SessionEntry | null = null;

/** Délai de grâce avant destruction, pour absorber le double montage de `StrictMode`. */
const RELEASE_GRACE_MS = 300;

function destroyEntry(entry: SessionEntry): void {
  if (entry.disposeTimer !== null) clearTimeout(entry.disposeTimer);
  if (currentEntry === entry) currentEntry = null;
  entry.session.dispose();
}

/**
 * Obtient la session d'une salle, en la créant au besoin.
 *
 * Un registre plutôt qu'une session créée directement dans un effet React : en mode
 * strict, React monte, démonte puis remonte chaque composant. Créer une session à chaque
 * montage ouvrirait **deux** connexions pour la même salle, et la seconde serait rejetée
 * par le serveur avec `role_taken` — l'utilisateur verrait alors une erreur alors que
 * tout fonctionne.
 *
 * Le point clé est que l'entrée **survit** au relâchement pendant un court délai : c'est
 * ce qui permet au remontage de retrouver la même session au lieu d'en créer une autre.
 */
export function acquireSession(roomId: string, key: Bytes, role: Role): ShareSession {
  const existing = currentEntry;

  if (existing && existing.roomId === roomId) {
    // Remontage immédiat : on reprend la session et on annule sa destruction.
    if (existing.disposeTimer !== null) {
      clearTimeout(existing.disposeTimer);
      existing.disposeTimer = null;
    }
    existing.references += 1;
    return existing.session;
  }

  // Changement de salle : l'ancienne session n'a plus de raison d'être.
  if (existing) destroyEntry(existing);

  const session = new ShareSession({ roomId, key, role });
  currentEntry = { roomId, session, references: 1, disposeTimer: null };
  void session.start();
  return session;
}

/**
 * Relâche une session. Elle n'est réellement détruite que si plus personne ne la détient,
 * et après un court délai qui absorbe un remontage immédiat.
 */
export function releaseSession(session: ShareSession): void {
  const entry = currentEntry;
  if (!entry || entry.session !== session) return;

  entry.references -= 1;
  if (entry.references > 0) return;

  // On **ne vide pas** `currentEntry` ici : l'entrée doit rester trouvable par un
  // éventuel remontage. C'est tout l'intérêt du délai de grâce.
  if (entry.disposeTimer !== null) clearTimeout(entry.disposeTimer);
  entry.disposeTimer = setTimeout(() => {
    if (currentEntry !== entry) return;
    destroyEntry(entry);
  }, RELEASE_GRACE_MS);
}

/** Nom d'appareil approximatif, affiché au correspondant. */
function guessDeviceName(): string | undefined {
  const agent = navigator.userAgent;
  if (/android/i.test(agent)) return 'Appareil Android';
  if (/iphone|ipad|ipod/i.test(agent)) return 'Appareil iOS';
  if (/macintosh|mac os x/i.test(agent)) return 'Mac';
  if (/windows/i.test(agent)) return 'PC Windows';
  if (/linux/i.test(agent)) return 'Machine Linux';
  return undefined;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Traduit un code d'erreur du serveur en message affichable.
 *
 * Le serveur envoie un **code** stable accompagné d'un texte : on privilégie toujours le
 * code, car lui seul est traduisible. Le texte ne sert que de repli pour un code qu'un
 * serveur plus récent introduirait sans que ce client le connaisse — c'est précisément
 * le cas que la validation tolérante du protocole doit couvrir.
 */
function translateServerError(code: ErrorCode, fallback: string): MessageDescriptor {
  switch (code) {
    case 'room_full':
      return msg.key('errors.roomFull');
    case 'role_taken':
      return msg.key('errors.roleTaken');
    case 'room_consumed':
      return msg.key('errors.roomConsumed');
    case 'rate_limited':
      return msg.key('errors.rateLimited');
    case 'unsupported_version':
      return msg.key('errors.unsupportedVersion');
    case 'relay_disabled':
      return msg.key('errors.relayDisabled');
    case 'payload_too_large':
      return msg.key('errors.payloadTooLarge');
    case 'bad_message':
      return msg.key('errors.badMessage');
    case 'internal':
      return msg.key('errors.internal');
    default:
      return msg.raw(fallback);
  }
}

/**
 * Associe un motif de refus reçu à son message traduisible.
 *
 * Le motif circule sous forme de code : c'est ce qui permet à un destinataire anglophone
 * de ne pas envoyer une phrase française à un émetteur français.
 */
function rejectReasonDescriptor(reason: RejectReason): MessageDescriptor {
  switch (reason) {
    case 'declined':
      return msg.key('errors.rejectDeclined');
    case 'destination-unavailable':
      return msg.key('errors.rejectNoDestination');
    case 'cannot-receive':
      return msg.key('errors.rejectCannotReceive');
  }
}
